import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import { find, isEmpty, map, sumBy } from "lodash";
import image from "../../assets/logo/authLogo.png";
import appConfig from "../config.json";
import { centrePdfText } from "../helpers/app/pdf";
import { numberWithCommas } from "../helpers/app/numberFormater";
import { toBulletText } from "../../components/common/BulletTextArea";

const fullName = (user) =>
  user ? `${user.first_name || ""} ${user.other_names || ""}`.trim() : "";

const formatDate = (date) => (date ? moment(date).format("DD/MM/YYYY") : "");

const n = (value) => Number(value) || 0;

const emptyRow = (text, colSpan) => [
  { content: text, colSpan, styles: { halign: "center", textColor: 120 } },
];

const loadImage = async (path) => {
  if (!path) return null;
  try {
    const response = await fetch(`${appConfig.server_url}${path}`);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const tableDefaults = {
  theme: "grid",
  margin: { left: 14, right: 14 },
  styles: {
    fontSize: 9,
    cellPadding: 2,
    overflow: "linebreak",
    valign: "top",
    textColor: 20,
  },
  headStyles: { fillColor: [41, 98, 155], textColor: 255, fontStyle: "bold" },
};

const sectionTitleStyle = { fontStyle: "bold", fillColor: [235, 240, 245] };
const boldCell = (content) => ({ content, styles: { fontStyle: "bold" } });

const ageGroups = ["0_12", "13_19", "20_35", "above_35"];
const beneficiaryGroups = [
  ["Male Ugandan", "male_ugandan"],
  ["Female Ugandan", "female_ugandan"],
  ["Male Refugee", "male_refugee"],
  ["Female Refugee", "female_refugee"],
];

const durationUnits = {
  hours: "Hour(s)",
  days: "Day(s)",
  weeks: "Week(s)",
  months: "Month(s)",
};

const generateActivityReportPdf = async (paymentVoucher, actualExpenditure) => {
  const report = paymentVoucher?.reportingTemplate || {};
  const doc = new jsPDF();
  const nextY = () => doc.lastAutoTable.finalY + 8;

  doc.setFontSize(10);
  doc.addImage(image, "PNG", 60, 15, 80, 25);
  centrePdfText(
    doc,
    " P.O.BOX 610029, Maracha, Uganda |Adongoro Cell, Maracha Town Council",
    45
  );
  centrePdfText(doc, "Mob: +256 393102379", 50);
  centrePdfText(
    doc,
    "Web: www.amaniinitiative.org  | Email: info.amaniinitiative@gmail.com  ",
    55
  );
  doc.setFontSize(12);
  centrePdfText(doc, "AMANI INITIATIVE ACTIVITY REPORT", 65);
  doc.setFontSize(10);

  const indicatorRows = map(
    report?.activityReportingTemplateIndicators,
    (indicator) => [
      find(paymentVoucher?.strategicPlanIndicators, {
        id: indicator.indicator_id,
      })?.indicator || "",
      numberWithCommas(indicator.score ?? 0),
    ]
  );

  const duration = report?.duration
    ? `${report.duration} ${durationUnits[report.duration_unit] || ""}`.trim()
    : "";

  doc.autoTable({
    ...tableDefaults,
    startY: 70,
    head: [["SUB-TITLES", "DESCRIPTION"]],
    body: [
      ["Project/Initiative Name", paymentVoucher?.projectDetails?.project_name || ""],
      ["Title of Activity", paymentVoucher?.budgetLineDetails?.name || ""],
      ["Funder", paymentVoucher?.projectDetails?.donor_name || ""],
      [
        "Dates and Duration",
        [
          report?.date ? `Start Date: ${formatDate(report.date)}` : "",
          duration ? `Duration: ${duration}` : "",
        ]
          .filter(Boolean)
          .join("\n"),
      ],
      ["Activity Venue", report?.venue || ""],
      ["Description of the Activity", toBulletText(report?.achievements_and_results)],
      ["Activity Achievements and Results", toBulletText(report?.description)],
      [
        "Challenges Encountered and How They Were Overcome",
        toBulletText(report?.challenges),
      ],
      ["Key Lessons", toBulletText(report?.key_lessons)],
      ["Recommendations", toBulletText(report?.recommendations)],
      ["Community Voice (Quotes)", report?.community_voice || ""],
      ["Success Story", report?.success_story || ""],
    ],
    columnStyles: { 0: { cellWidth: 55, ...sectionTitleStyle } },
  });

  doc.autoTable({
    ...tableDefaults,
    startY: nextY(),
    head: [
      [{ content: "Strategic Plan Indicators", colSpan: 2 }],
      ["Indicator", "Score"],
    ],
    body: isEmpty(indicatorRows)
      ? [emptyRow("No indicators selected", 2)]
      : indicatorRows,
    columnStyles: { 1: { cellWidth: 30, halign: "center" } },
  });

  const beneficiaryRows = map(beneficiaryGroups, ([label, prefix]) => {
    const values = map(ageGroups, (age) => n(report?.[`${prefix}_${age}`]));
    return [label, ...values, boldCell(sumBy(values))];
  });
  const columnTotals = map(ageGroups, (age) =>
    sumBy(beneficiaryGroups, ([, prefix]) => n(report?.[`${prefix}_${age}`]))
  );
  doc.autoTable({
    ...tableDefaults,
    startY: nextY(),
    head: [
      [
        {
          content: "Number of Project Participants or Intermediaries Reached",
          colSpan: 6,
        },
      ],
      ["Sex", "0-12", "13-19", "20-35", "Above 35", "Total"],
    ],
    body: [
      ...beneficiaryRows,
      [
        boldCell("Total"),
        ...map(columnTotals, boldCell),
        boldCell(sumBy(columnTotals)),
      ],
    ],
    columnStyles: {
      1: { halign: "center" },
      2: { halign: "center" },
      3: { halign: "center" },
      4: { halign: "center" },
      5: { halign: "center" },
    },
  });

  const disabilityMale = n(report?.persons_with_disability_male);
  const disabilityFemale = n(report?.persons_with_disability_female);
  doc.autoTable({
    ...tableDefaults,
    startY: nextY(),
    head: [
      [{ content: "Persons with a Disability", colSpan: 3 }],
      ["Male", "Female", "Total"],
    ],
    body: [
      [disabilityMale, disabilityFemale, boldCell(disabilityMale + disabilityFemale)],
    ],
    styles: { ...tableDefaults.styles, halign: "center" },
  });

  const actionPoints = report?.actionPoints || [];
  if (!isEmpty(actionPoints)) {
    doc.autoTable({
      ...tableDefaults,
      startY: nextY(),
      head: [
        [{ content: "Activity Follow Up and Action Points", colSpan: 4 }],
        ["Action Point", "Person Responsible", "Timeline", "Status"],
      ],
      body: map(actionPoints, (point) => [
        point.action_point || "",
        point.person_responsible || "",
        point.timeline || "",
        point.status || "",
      ]),
    });
  }

  doc.autoTable({
    ...tableDefaults,
    startY: nextY(),
    head: [[{ content: "Report Sign Off", colSpan: 2 }]],
    body: [
      ["Payment Voucher Number", paymentVoucher?.id ?? ""],
      [
        "Amount Advanced",
        `UGX ${numberWithCommas(paymentVoucher?.amountAdvanced ?? 0)}`,
      ],
      [
        "Amount Actually Used",
        `UGX ${numberWithCommas(
          sumBy(actualExpenditure, (item) => n(item.actual_expenditure))
        )}`,
      ],
      ["Staff In-Charge", fullName(report?.created_by)],
    ],
    columnStyles: { 0: { cellWidth: 55, ...sectionTitleStyle } },
  });

  const signatories = [
    ["Prepared By", report?.created_by, report?.created_at],
    ["Checked By", report?.checked_by, report?.checked_at],
    ["Approved By", report?.approved_by, report?.approved_at],
  ];
  const signatures = await Promise.all(
    map(signatories, ([, user]) => loadImage(user?.signature))
  );

  doc.autoTable({
    ...tableDefaults,
    startY: nextY(),
    head: [["", "Name", "Signature", "Date"]],
    body: map(signatories, ([label, user, date]) => [
      label,
      fullName(user),
      "",
      user ? formatDate(date) : "",
    ]),
    columnStyles: {
      0: { cellWidth: 35, ...sectionTitleStyle },
      2: { cellWidth: 45, minCellHeight: 14 },
    },
    didDrawCell: (hook) => {
      const signature = signatures[hook.row.index];
      if (hook.section !== "body" || hook.column.index !== 2 || !signature)
        return;
      try {
        doc.addImage(
          signature,
          hook.cell.x + 2,
          hook.cell.y + 1,
          hook.cell.width - 4,
          hook.cell.height - 2
        );
      } catch {
      }
    },
  });

  const pageCount = doc.internal.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFontSize(8);
    doc.text(
      `Page ${page} of ${pageCount}`,
      doc.internal.pageSize.width - 14,
      doc.internal.pageSize.height - 8,
      { align: "right" }
    );
  }

  doc.save(
    `ACTIVITY REPORT - ${paymentVoucher?.budgetLineDetails?.name || ""}.pdf`
  );
};

export default generateActivityReportPdf;
